/**
 * Sur API Service
 * Handles all API communication with the Sur backend
 */

import { config } from "../utils/config";
import { storage } from "../utils/storage";
import {
  LoginRequest,
  LoginResponse,
  User,
  CustomerSegment,
  MappingUploadRequest,
  MappingUploadResult,
  PaginatedResponse,
} from "../types";

/**
 * Convert snake_case keys to camelCase
 */
function snakeToCamel(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    const value = obj[key];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      result[camelKey] = snakeToCamel(value as Record<string, unknown>);
    } else {
      result[camelKey] = value;
    }
  }
  return result;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.apiBaseUrl;
  }

  /**
   * Make an authenticated API request
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = storage.getAccessToken();

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      mode: "cors",
    });

    // Handle token refresh on 401
    if (response.status === 401 && storage.getRefreshToken()) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Retry the request with new token
        const newToken = storage.getAccessToken();
        (headers as Record<string, string>)["Authorization"] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (!retryResponse.ok) {
          throw await this.handleError(retryResponse);
        }
        return retryResponse.json();
      } else {
        // Refresh failed, clear auth
        storage.clearAuth();
        throw new Error("Session expired. Please log in again.");
      }
    }

    if (!response.ok) {
      throw await this.handleError(response);
    }

    return response.json();
  }

  /**
   * Handle API errors
   */
  private async handleError(response: Response): Promise<Error> {
    let message = "An error occurred";
    try {
      const data = await response.json();
      message = data.detail || data.message || message;
    } catch {
      message = response.statusText || message;
    }
    return new Error(message);
  }

  /**
   * Login with email and password
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const rawResponse = await this.request<Record<string, unknown>>(
      config.endpoints.auth.login,
      {
        method: "POST",
        body: JSON.stringify(credentials),
      }
    );

    // Convert snake_case to camelCase
    const response = snakeToCamel(rawResponse) as unknown as LoginResponse;

    // Store tokens and user data
    storage.setAccessToken(response.accessToken);
    storage.setRefreshToken(response.refreshToken);
    storage.setUser(response.user);

    return response;
  }

  /**
   * Refresh the access token
   */
  async refreshToken(): Promise<boolean> {
    const refreshToken = storage.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(
        `${this.baseUrl}${config.endpoints.auth.refresh}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refreshToken }),
          mode: "cors",
        }
      );

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      storage.setAccessToken(data.accessToken);
      if (data.refreshToken) {
        storage.setRefreshToken(data.refreshToken);
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Logout and clear tokens
   */
  async logout(): Promise<void> {
    try {
      await this.request(config.endpoints.auth.logout, {
        method: "POST",
      });
    } catch {
      // Ignore logout errors
    } finally {
      storage.clearAuth();
    }
  }

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<User> {
    return this.request<User>(config.endpoints.auth.me);
  }

  /**
   * Get list of segments
   */
  async getSegments(): Promise<CustomerSegment[]> {
    const response = await this.request<PaginatedResponse<CustomerSegment>>(
      config.endpoints.segments.list
    );
    return response.data;
  }

  /**
   * Upload mappings as JSON
   */
  async uploadMappings(data: MappingUploadRequest): Promise<MappingUploadResult> {
    return this.request<MappingUploadResult>(config.endpoints.mappings.bulk, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /**
   * Upload mappings as CSV file
   */
  async uploadMappingsFile(
    file: Blob,
    replaceExisting = false
  ): Promise<MappingUploadResult> {
    const formData = new FormData();
    formData.append("file", file, "mappings.csv");
    if (replaceExisting) {
      formData.append("replace_existing", "true");
    }

    const url = `${this.baseUrl}${config.endpoints.mappings.upload}`;
    const token = storage.getAccessToken();

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
      mode: "cors",
    });

    if (!response.ok) {
      throw await this.handleError(response);
    }

    return response.json();
  }

  /**
   * Get mapping summary (counts per segment)
   */
  async getMappingSummary(): Promise<{ segmentName: string; count: number }[]> {
    return this.request(config.endpoints.mappings.summary);
  }
}

export const api = new ApiService();
export default api;
